import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
  LogOut, LayoutDashboard, FilePlus2, Users, ClipboardList,
  ShieldCheck, PackageCheck, BarChart3, RefreshCw, Menu, X,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "./NotificationBell";
import {
  canAccessAdmin, canAccessHod, canAccessProcessOwner,
  canSeeAnalytics, canSeeTransportTracker, canSeeGuestTracker, canSeeFnbTracker, canSeePrintJobTracker, isSuperAdmin,
} from "@/lib/roles";

/**
 * Phase 3c — header cleanup:
 * - Logo only (no sibling school-name text; the logo already carries it).
 * - Nav label "Fulfilment" → "Fulfilment (PO)" for clarity.
 * - Fully responsive: inline nav on desktop (≥1200px), hamburger drawer on
 *   tablet & mobile with focus trap, Escape-to-close, and backdrop dismiss.
 */
export default function Header({ onOpenRefresh }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  // Close drawer on route change (NavLink click). We listen for a custom
  // <a>-click event by watching the drawer container.
  const closeDrawer = () => setDrawerOpen(false);

  // Escape to close + basic focus trap.
  useEffect(() => {
    if (!drawerOpen) return;
    const previouslyFocused = document.activeElement;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); closeDrawer(); }
      if (e.key === "Tab") {
        const container = drawerRef.current;
        if (!container) return;
        const focusables = container.querySelectorAll(
          'a, button, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault(); last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault(); first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    // focus first item
    setTimeout(() => {
      drawerRef.current?.querySelector("a,button")?.focus();
    }, 30);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        try { previouslyFocused.focus(); } catch { /* noop */ }
      }
    };
  }, [drawerOpen]);

  const linkCls = ({ isActive }) =>
    `px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
      isActive ? "text-[#7a1f2b] bg-[#f4e9eb]" : "text-[#4a524c] hover:bg-[#faf5eb]"
    }`;

  const drawerLinkCls = ({ isActive }) =>
    `flex items-center gap-2 px-4 py-3 rounded-md text-[15px] font-medium border-l-4 transition-colors ${
      isActive
        ? "text-[#7a1f2b] bg-[#f4e9eb] border-[#7a1f2b]"
        : "text-[#1f2320] hover:bg-[#faf5eb] border-transparent"
    }`;

  const navItems = (
    <>
      <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">
        <LayoutDashboard size={16} className="inline mr-1" /> Dashboard
      </NavLink>
      <NavLink to="/requisitions/new" className={linkCls} data-testid="nav-new">
        <FilePlus2 size={16} className="inline mr-1" /> New
      </NavLink>
      {canAccessHod(user) && (
        <NavLink to="/hod" className={linkCls} data-testid="nav-hod">
          <ClipboardList size={16} className="inline mr-1" /> HOD Queue
        </NavLink>
      )}
      {canAccessAdmin(user) && (
        <NavLink to="/admin" className={linkCls} data-testid="nav-admin">
          <ShieldCheck size={16} className="inline mr-1" /> Admin
        </NavLink>
      )}
      {canAccessProcessOwner(user) && (
        <NavLink
          to="/process-owner"
          className={linkCls}
          data-testid="nav-po"
          aria-label="Fulfilment (Process Owner) queue"
          title="Fulfilment (Process Owner) queue"
        >
          <PackageCheck size={16} className="inline mr-1" /> Fulfilment (PO)
        </NavLink>
      )}
      {canSeeAnalytics(user) && (
        <NavLink to="/analytics" className={linkCls} data-testid="nav-analytics">
          <BarChart3 size={16} className="inline mr-1" /> Analytics
        </NavLink>
      )}
      {canSeeTransportTracker(user) && (
        <NavLink to="/analytics/transport-tracker" className={linkCls} data-testid="nav-transport-tracker">
          <BarChart3 size={16} className="inline mr-1" /> Transport Tracker
        </NavLink>
      )}
      {canSeeGuestTracker(user) && (
        <NavLink to="/analytics/guest-tracker" className={linkCls} data-testid="nav-guest-tracker">
          <BarChart3 size={16} className="inline mr-1" /> Guest Tracker
        </NavLink>
      )}
      {canSeeFnbTracker(user) && (
        <NavLink to="/analytics/fnb-tracker" className={linkCls} data-testid="nav-fnb-tracker">
          <BarChart3 size={16} className="inline mr-1" /> Cake/F&amp;B Tracker
        </NavLink>
      )}
      {canSeePrintJobTracker(user) && (
        <NavLink to="/analytics/print-job-tracker" className={linkCls} data-testid="nav-print-job-tracker">
          <BarChart3 size={16} className="inline mr-1" /> Print Job Tracker
        </NavLink>
      )}
      {isSuperAdmin(user) && (
        <NavLink to="/admin/users" className={linkCls} data-testid="nav-admin-users">
          <Users size={16} className="inline mr-1" /> Users
        </NavLink>
      )}
      {isSuperAdmin(user) && (
        <NavLink to="/admin/workflow-repair" className={linkCls} data-testid="nav-workflow-repair">
          <ShieldCheck size={16} className="inline mr-1" /> Workflow Repair
        </NavLink>
      )}
    </>
  );

  const drawerNavItems = (
    <>
      <NavLink to="/dashboard" className={drawerLinkCls} onClick={closeDrawer} data-testid="drawer-nav-dashboard">
        <LayoutDashboard size={18} /> Dashboard
      </NavLink>
      <NavLink to="/requisitions/new" className={drawerLinkCls} onClick={closeDrawer} data-testid="drawer-nav-new">
        <FilePlus2 size={18} /> New requisition
      </NavLink>
      {canAccessHod(user) && (
        <NavLink to="/hod" className={drawerLinkCls} onClick={closeDrawer} data-testid="drawer-nav-hod">
          <ClipboardList size={18} /> HOD Queue
        </NavLink>
      )}
      {canAccessAdmin(user) && (
        <NavLink to="/admin" className={drawerLinkCls} onClick={closeDrawer} data-testid="drawer-nav-admin">
          <ShieldCheck size={18} /> Admin
        </NavLink>
      )}
      {canAccessProcessOwner(user) && (
        <NavLink to="/process-owner" className={drawerLinkCls} onClick={closeDrawer}
                 data-testid="drawer-nav-po"
                 aria-label="Fulfilment (Process Owner) queue">
          <PackageCheck size={18} /> Fulfilment (PO)
        </NavLink>
      )}
      {canSeeAnalytics(user) && (
        <NavLink to="/analytics" className={drawerLinkCls} onClick={closeDrawer} data-testid="drawer-nav-analytics">
          <BarChart3 size={18} /> Analytics
        </NavLink>
      )}
      {isSuperAdmin(user) && (
        <NavLink to="/admin/users" className={drawerLinkCls} onClick={closeDrawer} data-testid="drawer-nav-admin-users">
          <Users size={18} /> Users
        </NavLink>
      )}
      {isSuperAdmin(user) && (
        <NavLink to="/admin/workflow-repair" className={drawerLinkCls} onClick={closeDrawer} data-testid="drawer-nav-workflow-repair">
          <ShieldCheck size={18} /> Workflow Repair
        </NavLink>
      )}
    </>
  );

  return (
    <header className="bg-white/95 backdrop-blur-md border-b border-[#e2d7c3] sticky top-0 z-40" data-testid="app-header">
      <div className="max-w-7xl mx-auto flex items-center gap-3 sm:gap-4 px-3 sm:px-4 md:px-6 py-3 flex-nowrap">
        {/* Logo only — the emblem already carries the school name */}
        <Link
          to="/dashboard"
          className="flex items-center shrink-0"
          data-testid="header-home-link"
          aria-label="Vishwashanti Gurukul World School — go to dashboard"
        >
          <img
            src="/vgws-logo.png"
            alt="Vishwashanti Gurukul World School"
            className="rounded-full shadow-sm ring-1 ring-[#e2d7c3] h-8 w-8 sm:h-9 sm:w-9 md:h-11 md:w-11 shrink-0"
          />
        </Link>

        {/* Centered portal title — w-0 forces flex to allow this column to
            shrink below its content width, so truncate can kick in when the
            desktop nav is wide (super_admin has 7 items). */}
        <div className="w-0 flex-1 flex flex-col items-center min-w-0 px-2">
          <div className="font-serif text-base sm:text-lg xl:text-xl font-semibold text-[#1f2320] leading-tight truncate max-w-full">
            Requisition Portal
          </div>
        </div>

        {/* Desktop inline nav (≥1200px) */}
        <nav className="hidden xl:flex items-center gap-1 shrink-0 flex-nowrap" data-testid="header-nav-desktop">
          {navItems}
          {isSuperAdmin(user) && <SuperAdminChip />}
        </nav>

        {/* Notification bell (always visible; sits before drawer trigger) */}
        <NotificationBell />

        {/* Desktop-only refresh + user pill + logout */}
        {onOpenRefresh && (
          <button
            onClick={onOpenRefresh}
            className="hidden xl:inline-flex p-2 rounded-md text-[#4a524c] hover:bg-[#faf5eb] transition-colors shrink-0"
            title="Refresh app cache"
            data-testid="header-refresh-app"
          >
            <RefreshCw size={18} />
          </button>
        )}

        <div className="hidden xl:flex items-center gap-2 shrink-0 border-l border-[#e2d7c3] pl-3 ml-1">
          <div className="text-right leading-tight">
            <div className="text-sm font-semibold text-[#1f2320] max-w-[160px] truncate" data-testid="header-user-name">{user?.name}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#2d6b6b]">{user?.role}</div>
          </div>
          <div
            className="w-9 h-9 rounded-full bg-[#7a1f2b] text-white flex items-center justify-center font-serif font-bold shadow-sm shrink-0"
            data-testid="header-avatar"
          >
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <button
            onClick={handleLogout}
            className="p-2 rounded-md text-[#5a1520] hover:bg-[#f4e9eb] transition-colors shrink-0"
            title="Log out"
            data-testid="logout-button"
          >
            <LogOut size={18} />
          </button>
        </div>

        {/* Mobile / tablet hamburger — visible below xl breakpoint */}
        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="xl:hidden inline-flex items-center justify-center p-2 rounded-md text-[#4a524c] hover:bg-[#faf5eb] transition-colors shrink-0"
          aria-label="Open menu"
          aria-expanded={drawerOpen}
          aria-controls="mobile-drawer"
          data-testid="header-menu-open"
        >
          <Menu size={22} />
        </button>
      </div>

      {/* Right-side drawer */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-50 xl:hidden"
          data-testid="mobile-drawer-backdrop"
          onClick={closeDrawer}
        >
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" aria-hidden="true" />
          <aside
            ref={drawerRef}
            id="mobile-drawer"
            className="absolute right-0 top-0 h-full w-[86%] max-w-[340px] bg-white shadow-2xl border-l border-[#e2d7c3] flex flex-col animate-in slide-in-from-right duration-200"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Main menu"
            data-testid="mobile-drawer"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#e2d7c3]">
              <div className="flex items-center gap-2">
                <img
                  src="/vgws-logo.png"
                  alt=""
                  className="w-8 h-8 rounded-full ring-1 ring-[#e2d7c3]"
                />
                <span className="font-serif text-base font-semibold text-[#1f2320]">Requisition Portal</span>
              </div>
              <button
                type="button"
                onClick={closeDrawer}
                className="p-2 rounded-md text-[#4a524c] hover:bg-[#faf5eb]"
                aria-label="Close menu"
                data-testid="header-menu-close"
              >
                <X size={20} />
              </button>
            </div>

            {isSuperAdmin(user) && (
              <div className="px-4 pt-3">
                <SuperAdminChip />
              </div>
            )}

            <nav className="flex flex-col gap-1 p-3 overflow-y-auto flex-1" aria-label="Primary navigation">
              {drawerNavItems}
            </nav>

            <div className="border-t border-[#e2d7c3] p-3 flex flex-col gap-2 bg-[#faf5eb]">
              <div className="flex items-center gap-3 px-1 py-1">
                <div
                  className="w-9 h-9 rounded-full bg-[#7a1f2b] text-white flex items-center justify-center font-serif font-bold shadow-sm shrink-0"
                  data-testid="drawer-avatar"
                >
                  {(user?.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#1f2320] truncate" data-testid="drawer-user-name">{user?.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#2d6b6b]">{user?.role}</div>
                </div>
              </div>
              {onOpenRefresh && (
                <button
                  type="button"
                  onClick={() => { closeDrawer(); onOpenRefresh(); }}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[#4a524c] hover:bg-white text-sm"
                  data-testid="drawer-refresh-app"
                >
                  <RefreshCw size={16} /> Refresh app cache
                </button>
              )}
              <button
                type="button"
                onClick={() => { closeDrawer(); handleLogout(); }}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-[#5a1520] hover:bg-white text-sm font-medium"
                data-testid="drawer-logout"
              >
                <LogOut size={16} /> Log out
              </button>
            </div>
          </aside>
        </div>
      )}
    </header>
  );
}

function SuperAdminChip() {
  return (
    <span
      className="ml-2 inline-block px-2 py-0.5 rounded-full text-[11px] uppercase tracking-widest font-semibold bg-slate-200 text-slate-700 border border-slate-300 align-middle whitespace-nowrap shrink-0"
      title="Super Admin — you are viewing all users' queues across HOD / Admin / Fulfilment (PO)"
      data-testid="super-admin-chip"
    >
      Super Admin
    </span>
  );
}
